import React, { useEffect, useRef, useState } from 'react';
import { Loader2, FileWarning } from "lucide-react";
import * as pdfjsLib from 'pdfjs-dist';
import pdfWorkerUrl from 'pdfjs-dist/build/pdf.worker.min.mjs?url';

// PDF.js needs a worker to parse documents off the main thread. Vite resolves the
// bundled worker via ?url so it is served from our own origin (no CDN dependency).
pdfjsLib.GlobalWorkerOptions.workerSrc = pdfWorkerUrl;

interface PdfCanvasViewerProps {
    /** Raw base64 PDF payload (without the data: prefix) */
    base64: string;
    className?: string;
    /**
     * Fired once every page has been painted. The parent needs this because the
     * canvases are appended asynchronously — only after this fires does the
     * scroll container have its final scrollHeight (used to compute read
     * progress and to detect a document that is short enough to need no
     * scrolling at all).
     */
    onRendered?: () => void;
    /**
     * Fired when the document cannot be rasterised at all.
     *
     * The parent MUST react to this: it gates the accept-checkbox / eSign block
     * on `onRendered`, so without this hook a render failure left the user on a
     * dead screen with no way to continue the journey. Known trigger in the
     * wild: older mobile browsers (e.g. the Samsung Internet build shipped on
     * many Android phones) lack `Promise.withResolvers`, which pdfjs-dist v4
     * calls internally — the document throws before a single page is painted.
     */
    onError?: () => void;
    /**
     * Zoom is owned by the parent so its controls can be rendered as an overlay
     * pinned to the viewport. Keeping them inside this component breaks once the
     * page container is wider than the screen — a centred element would sit in
     * the middle of the zoomed width, i.e. off-screen.
     */
    zoom: number;
    onZoomChange: (zoom: number) => void;
}

export const MIN_ZOOM = 1;
export const MAX_ZOOM = 3;

/**
 * Resolution multiplier used when rasterising. Pages are painted once at this
 * many times their fit-to-width size, so zooming is pure CSS scaling of an
 * already-sharp bitmap — no re-render, no flicker, no scroll jump. MAX_ZOOM is
 * matched so text is still crisp when fully zoomed in.
 */
const RENDER_SCALE = MAX_ZOOM;

/**
 * Renders every page of a PDF onto <canvas> elements using PDF.js.
 *
 * Why not <iframe src="data:application/pdf;base64,...">?
 * Mobile browsers ship no inline PDF viewer plugin, and Chrome blocks data: URIs
 * for plugin resources - so the iframe approach shows a broken-file icon or an
 * "Open" prompt on Android/iOS. Painting to canvas works identically everywhere.
 *
 * Zoom: the document is rasterised ONCE at RENDER_SCALE and then simply displayed
 * wider/narrower. An earlier version re-rasterised on every zoom change, which
 * blanked the container, reset the scroll position back to page 1 and felt
 * glitchy. Rendering high-res up front trades a little memory for a viewer that
 * behaves like a real PDF reader.
 */
const PdfCanvasViewer: React.FC<PdfCanvasViewerProps> = ({
    base64,
    className = "",
    onRendered,
    onError,
    zoom,
    onZoomChange,
}) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const rootRef = useRef<HTMLDivElement>(null);
    const [rendering, setRendering] = useState(true);
    // Boolean, not the error string: the raw pdfjs message ("Promise.withResolvers
    // is not a function", "Invalid PDF structure", …) is meaningless to a borrower
    // and looked like a crash. We show one fixed, actionable message instead and
    // keep the technical detail in the console for support.
    const [renderFailed, setRenderFailed] = useState(false);

    // ── Pinch handling ────────────────────────────────────────────────────────
    // Native listeners with { passive: false } are required: React's synthetic
    // touch handlers cannot reliably preventDefault() the browser's own pinch,
    // which would otherwise zoom the whole app chrome instead of the document.
    //
    // Smoothness: during the gesture the pages are scaled with a CSS `transform`,
    // which the compositor handles on the GPU without touching layout. Changing
    // the container's `width` on every touchmove (the previous approach) forced a
    // full reflow of every canvas each frame and felt visibly janky. The width is
    // committed only once, on release, and the transform is dropped at the same
    // time — so the end result is identical but the gesture stays at 60fps.
    useEffect(() => {
        const el = rootRef.current;
        if (!el) return;

        let startDist = 0;
        let startZoom = 1;
        let pinching = false;
        let latestScale = 1;
        let frame = 0;

        const distance = (t: TouchList) =>
            Math.hypot(t[0].clientX - t[1].clientX, t[0].clientY - t[1].clientY);

        const paint = () => {
            frame = 0;
            const node = containerRef.current;
            if (node) node.style.transform = latestScale === 1 ? '' : `scale(${latestScale})`;
        };

        const onTouchStart = (e: TouchEvent) => {
            if (e.touches.length !== 2) return;
            pinching = true;
            startDist = distance(e.touches);
            startZoom = zoom;
            latestScale = 1;
            // Skip transition/AA work mid-gesture.
            const node = containerRef.current;
            if (node) node.style.willChange = 'transform';
        };

        const onTouchMove = (e: TouchEvent) => {
            if (!pinching || e.touches.length !== 2 || startDist === 0) return;
            e.preventDefault(); // block native page zoom + scroll mid-pinch
            const target = Math.min(
                MAX_ZOOM,
                Math.max(MIN_ZOOM, startZoom * (distance(e.touches) / startDist))
            );
            // Expressed relative to the committed zoom so transform and width
            // never compound.
            latestScale = target / startZoom;
            // One paint per animation frame, no matter how often touchmove fires.
            if (!frame) frame = requestAnimationFrame(paint);
        };

        const onTouchEnd = (e: TouchEvent) => {
            if (e.touches.length >= 2 || !pinching) return;
            pinching = false;
            if (frame) { cancelAnimationFrame(frame); frame = 0; }

            const committed = Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, startZoom * latestScale));
            const node = containerRef.current;
            if (node) {
                // Drop the transform in the same frame React applies the new width,
                // otherwise the two briefly multiply and the page visibly jumps.
                node.style.transform = '';
                node.style.willChange = '';
            }
            latestScale = 1;
            startDist = 0;
            if (committed !== zoom) onZoomChange(committed);
        };

        el.addEventListener('touchstart', onTouchStart, { passive: false });
        el.addEventListener('touchmove', onTouchMove, { passive: false });
        el.addEventListener('touchend', onTouchEnd);
        el.addEventListener('touchcancel', onTouchEnd);
        return () => {
            if (frame) cancelAnimationFrame(frame);
            el.removeEventListener('touchstart', onTouchStart);
            el.removeEventListener('touchmove', onTouchMove);
            el.removeEventListener('touchend', onTouchEnd);
            el.removeEventListener('touchcancel', onTouchEnd);
        };
    }, [zoom, onZoomChange]);

    // ── Rasterisation ─────────────────────────────────────────────────────────
    // Runs ONCE per document. Deliberately does NOT depend on zoom.
    useEffect(() => {
        let cancelled = false;
        // Tracks the document so we can release memory when props change/unmount
        let pdfDoc: pdfjsLib.PDFDocumentProxy | null = null;

        const render = async () => {
            setRendering(true);
            setRenderFailed(false);

            try {
                // base64 -> Uint8Array (atob gives us a binary string)
                const binary = atob(base64);
                const bytes = new Uint8Array(binary.length);
                for (let i = 0; i < binary.length; i++) {
                    bytes[i] = binary.charCodeAt(i);
                }

                pdfDoc = await pdfjsLib.getDocument({ data: bytes }).promise;
                if (cancelled) return;

                const container = containerRef.current;
                if (!container) return;
                container.innerHTML = '';

                // Measure the parent: the container's own width is driven by zoom,
                // so reading it here would feed zoom back into the bitmap size.
                const baseWidth = container.parentElement?.clientWidth || container.clientWidth;

                // Cap the device pixel ratio: combined with RENDER_SCALE this is
                // already a large bitmap, and high-DPI phones would otherwise run
                // out of canvas memory on a long agreement.
                const dpr = Math.min(window.devicePixelRatio || 1, 1.5);

                for (let pageNum = 1; pageNum <= pdfDoc.numPages; pageNum++) {
                    if (cancelled) return;

                    const page = await pdfDoc.getPage(pageNum);
                    const unscaled = page.getViewport({ scale: 1 });

                    // Rasterise at RENDER_SCALE x fit-to-width, then let CSS size it
                    // down to 100%. Zooming in later reveals real pixels, not blur.
                    const targetWidth = (baseWidth || unscaled.width) * RENDER_SCALE;
                    const scale = targetWidth / unscaled.width;
                    const viewport = page.getViewport({ scale: scale * dpr });

                    const canvas = document.createElement('canvas');
                    canvas.width = viewport.width;
                    canvas.height = viewport.height;
                    canvas.style.width = '100%';
                    canvas.style.height = 'auto';
                    canvas.style.display = 'block';
                    canvas.style.marginBottom = '8px';

                    const context = canvas.getContext('2d');
                    if (!context) continue;

                    container.appendChild(canvas);
                    await page.render({ canvasContext: context, viewport }).promise;
                }

                if (!cancelled) {
                    setRendering(false);
                    onRendered?.();
                }
            } catch (err: any) {
                // Keep the real reason for support/debugging — it is never shown
                // to the borrower (see renderFailed).
                console.error('PDF render error:', err);
                if (!cancelled) {
                    setRenderFailed(true);
                    setRendering(false);
                    // Unblock the journey: tells the parent to reveal the accept
                    // checkbox + eSign button even though no page was painted.
                    onError?.();
                }
            }
        };

        render();

        return () => {
            cancelled = true;
            pdfDoc?.destroy();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [base64]);

    return (
        <div
            ref={rootRef}
            className={`relative ${className}`}
            // Pinch is handled by the native listeners above. Both pan axes must
            // stay enabled — 'pan-y' alone silently kills the sideways drag you
            // need to read a zoomed-in page.
            style={{ touchAction: 'pan-x pan-y' }}
        >
            {/* Spinner only (no label) while pages rasterise, so the viewport is
                never blank between the PDF arriving and the first canvas being
                painted. */}
            {rendering && (
                <div className="flex items-center justify-center py-10">
                    <Loader2 className="h-6 w-6 animate-spin text-intellend-green" />
                </div>
            )}

            {renderFailed && !rendering && (
                <div className="px-4 py-8 text-center">
                    <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-amber-100 text-amber-600">
                        <FileWarning className="h-6 w-6" />
                    </div>
                    <p className="text-base font-semibold text-gray-900">
                        Failed to Load PDF
                    </p>
                    <p className="mx-auto mt-1 max-w-xs text-sm text-gray-500">
                        You can download this PDF or use Chrome for the best experience.
                    </p>
                </div>
            )}

            {/* Zoom is just this width changing — the canvases inside are 100% of
                it. The parent scroll container pans once it exceeds the viewport. */}
            <div
                ref={containerRef}
                className="bg-gray-100 origin-top-left"
                style={{ width: `${zoom * 100}%` }}
            />
        </div>
    );
};

export default PdfCanvasViewer;
